import { useCallback, useEffect } from "react";
import { useSearchParams } from "react-router";
import { toggleSort, withParams } from "@/lib/list-url";

/** The page's URL parameters, and a setter that changes some of them. */
export function useListUrl(): [URLSearchParams, (changes: Record<string, string | null>) => void] {
  const [url, setUrl] = useSearchParams();
  const change = useCallback(
    (changes: Record<string, string | null>) => setUrl((current) => withParams(current, changes)),
    [setUrl]
  );
  return [url, change];
}

/** When the server served a different page than the URL asked for (a page
 *  past the end, after rows moved away), the URL follows the server.
 *
 *  Callers MUST pass `undefined` while `query.isPlaceholderData` is true:
 *  placeholder data is the previous page. With keepPreviousData, clicking
 *  Next briefly serves the old page, and correcting from it would revert the
 *  URL so the pager never advances. */
export function usePageCorrection(name: string, served: number | undefined): void {
  const [url, setUrl] = useSearchParams();
  const asked = url.get(name);
  useEffect(() => {
    if (served === undefined || asked === null || asked === String(served)) return;
    setUrl((current) => withParams(current, { [name]: served === 1 ? null : String(served) }), { replace: true });
  }, [asked, name, served, setUrl]);
}

/** The `onSort` handler for a SortableTable: toggles the sort on the URL and
 *  returns the list to page 1. Pass a section's own parameter names on a
 *  page with several lists. Every list sorts by name until told otherwise, in
 *  its table and on the server, so `defaultSort` is "name". */
export function useSortHandler(
  names?: { sort: string; dir: string; page: string },
  defaultSort = "name"
): (key: string) => void {
  const [, setUrl] = useSearchParams();
  const sortName = names?.sort;
  const dirName = names?.dir;
  const pageName = names?.page;
  return useCallback(
    (key: string) =>
      setUrl((current) =>
        toggleSort(current, key, sortName && dirName && pageName ? { sort: sortName, dir: dirName, page: pageName } : undefined,
          defaultSort
        )
      ),
    [setUrl, sortName, dirName, pageName, defaultSort]
  );
}
